import React, { createContext, useState, useEffect, useRef, useContext } from 'react';
import { DocumentContext } from './ContextDocument';
import A4_MM_DIMENSION from '../../../enums/printPageA4SizeEnum';
import _uniqueId from 'lodash/uniqueId';
import './styles.css';

/**
 * Page serves as a context wrapper. It provides the context for sections
 * within the scope of this specific page, so they are aware of their page number. It also applies
 * a style to resemble an A4 document.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child elements to be included in the page.
 * @param {string} props.name - The name of the page.
 * @param {string} [props.id='page'] - The id attribute of the wrapping div.
 *
 * @returns {React.Element} The PageContext.Provider element with the page div and children wrapped inside.
 *
 * @example
 *  <Page id='page1' name='Page 1'>
 *    <Section id='section1' name='Introduction'/>
 *  </Page>
 *
 * The Page component provides the following context values:
 * - pageNumber: the number of the current page.
 */
export const PageContext = createContext({
  pageNumber: 0,
});

export function Page({ children, name, id = 'page', ...props }) {
  const { pages, addPage, deletePage } = useContext(DocumentContext);

  const [pageNumber, setPageNumber] = useState(0);
  const { current: pageId } = useRef(_uniqueId('page-'));

  const pageStyle = {
    width: `${A4_MM_DIMENSION.width * 1.5}mm`,
    minHeight: `${A4_MM_DIMENSION.height * 1.5}mm`,
    height: `${A4_MM_DIMENSION.height * 1.5}mm`,
    padding: '0mm 25mm',
    backgroundColor: 'white',
    overflow: 'hidden',
  };

  useEffect(() => {
    const nameArray = name.split('-');
    const root = nameArray[0];
    let overflow = 0;

    if (nameArray[1] === 'overflow') {
      overflow = parseInt(nameArray[2]);
    }

    addPage({ id: pageId, root, overflow });

    // Return a cleanup function that gets called on unmount
    return () => {
      // delete the page when the component unmounts
      deletePage(pageId);
    };
  }, []);

  useEffect(() => {
    const atPageIndex = pages.findIndex((page) => page.id === pageId);
    setPageNumber(atPageIndex + 1);
  }, [pages]);

  const contextValue = {
    pageNumber,
  };

  return (
    <PageContext.Provider value={contextValue}>
      <div className="page">
        <div id={id} className="page-container" style={pageStyle} {...props}>
          {children}
        </div>
      </div>
    </PageContext.Provider>
  );
}
